// CResultTileScene — 牌局結束畫面 1／3：各家狀態（攤牌）
// 對應反編譯原版 GameResultTileScene。
//
// 內容：4 列玩家（依 seat），每列 = header（頭像+座位字+風字+莊+名字）+ 攤開手牌
// （副露 + concealed + 獨立胡牌張 + 花），胡家右側貼「自摸／胡」章、放槍者貼「放槍」章。
// 底部「請碰觸螢幕繼續」閃爍；點任意處 / 按任意鍵 → 下一畫面
// （胡 → ResultPoint 算台；流局 → 直接 ResultMoney 籌碼）。
//
// 預覽：?scene=ResultTile（無 payload → 注入 mock）。
//
// === 使用圖片 ===
// - 標題：page-skin（Text），各家內容直接呈現在桌面，無對話框。
// - 桌背景 + 遮罩 + header 圖：見 CBaseResultScene 檔頭
// - 手牌 / 副露 / 花：CTileSprite（face=HandBottom；圖片見 tile-sprite.ts）
// - public/assets/ui/result-tag-self-draw.png（key: RESULT_TAG_SELF_DRAW_KEY，1080×792）— 4×「自摸」章
// - public/assets/ui/result-tag-hu.png（key: RESULT_TAG_HU_KEY，1080×792）— 4×「胡」章
// - public/assets/ui/result-tag-pao.png（key: RESULT_TAG_PAO_KEY，1080×792）— 4×「放槍」章
// - public/assets/ui/result-draw.png（key: RESULT_DRAW_KEY）— 流局指示（僅流局時顯示）

import { createPageTitle, PAGE_THEME } from '@ui/page-skin';
import { SCREEN_CENTER_X, LOGICAL_HEIGHT, LOGICAL_WIDTH } from '@config/game-config';
import { RoundSummaryKind } from '@core/summary';
import type { TSeat } from '@core/types';
import type { THand } from '@core/hand';
import { MeldType } from '@core/meld';
import type { TMeld } from '@core/meld';
import type { TTile } from '@core/tile';
import { CTileSprite, TileFace, FONT_STYLES, TILE_BODY_WIDTH } from '@ui/index';

import { CBaseResultScene } from './base-result-scene';
import {
  RESULT_DRAW_KEY,
  RESULT_TAG_HU_KEY,
  RESULT_TAG_PAO_KEY,
  RESULT_TAG_SELF_DRAW_KEY,
} from './boot-scene';
import { SceneKey } from './scene-keys';
import { playUIClick } from '@audio/scene-audio';
import type { TResultFlowData } from './result-flow-data';
import { splitResultHand } from './result-tile-layout';

// ============================================================================
// 版面常數
// ============================================================================
const ROW_TOP_MARGIN = 108; // 第一列距畫面頂端
const ROW_HEIGHT = 132; // 每列高度
const HEADER_OFFSET_Y = 20; // header 中心距列頂
const TILES_OFFSET_Y = 92; // 手牌中心距列頂

const TAG_SCALE = 0.1125; // 素材 Upscayl 4×；顯示尺寸維持原本 0.45 倍

const CONTENT_X = 114; // 原 APP 一般解析度四家內容起始 X

const HEADER_SCALE = 0.6; // header 縮放

const TILE_SCALE = 0.36; // 攤開手牌縮放
const TILE_STEP = TILE_BODY_WIDTH * TILE_SCALE; // 牌寬 + 小縫隙（不重疊、排排站）
const MELD_GAP = 4; // 原 APP 各副露組之間僅留極小間隔
const WIN_TILE_GAP = 20; // 第 17 張胡牌張與一般暗牌之間的獨立間隔
const FLOWER_GROUP_GAP = 20; // 依使用者指定，花牌接在整列最後
const KONG_STACK_OFFSET_Y = 24; // 槓的第四張疊在中間牌上方
const TAG_GAP = 70; // 結果章接在牌列右側
const TAG_RIGHT_MARGIN = 72; // 防止結果章超出畫面

const PRESS_HINT_Y = LOGICAL_HEIGHT - 60; // 「請碰觸螢幕繼續」Y
const PRESS_HINT_TEXT = '《 請碰觸螢幕繼續 》';
const PRESS_HINT_FONT_SIZE = '28px';

// ---- 流局指示（result-draw 126×126，僅流局時疊在四家攤牌中央）----
const DRAW_BANNER_Y = LOGICAL_HEIGHT / 2; // 流局圖中心 Y（畫面正中）
const DRAW_BANNER_SCALE = 2.4; // 流局圖放大倍率（原圖偏小）
const DRAW_BANNER_DEPTH = 50; // 高於攤牌牌面，蓋在中央

const TITLE_Y = 78; // 攤牌頁標題

export class CResultTileScene extends CBaseResultScene {
  constructor() {
    super({ key: SceneKey.ResultTile });
  }

  init(data?: TResultFlowData): void {
    this.resolveFlow(data);
  }

  create(): void {
    this.createBackdrop();
    createPageTitle(this, SCREEN_CENTER_X, TITLE_Y, '四家攤牌');

    for (let seat = 0 as TSeat; seat < 4; seat = (seat + 1) as TSeat) {
      this.renderRow(seat);
    }

    // 流局：四家無胡 / 放槍章 → 中央疊上「流局」指示圖，否則玩家看不出本局是和局
    if (this.flow.summary.kind === RoundSummaryKind.Draw) {
      this.renderDrawBanner();
    }

    this.renderPressHint();

    // 點任意處 / 按任意鍵 → 下一畫面
    this.input.once('pointerdown', () => this.advance());
    this.input.keyboard?.once('keydown', () => this.advance());
  }

  // --------------------------------------------------------------------------
  // 單列：header + 攤牌 + 胡/放槍章
  // --------------------------------------------------------------------------
  private renderRow(seat: TSeat): void {
    const player = this.flow.players[seat];
    if (!player) return;
    const rowTop = ROW_TOP_MARGIN + seat * ROW_HEIGHT;

    this.renderPlayerHeader(CONTENT_X, rowTop + HEADER_OFFSET_Y, player, HEADER_SCALE);
    const tilesEndX = this.renderHandTiles(
      CONTENT_X,
      rowTop + TILES_OFFSET_Y,
      player.hand,
      this.getWinTileId(seat)
    );
    this.renderTag(seat, tilesEndX, rowTop + ROW_HEIGHT / 2 + 8);
  }

  /**
   * 原 APP 排法：副露佔前方固定槽 → 暗牌靠後 → 胡牌張留縫；
   * 依使用者指定，花牌仍接在整列最後。
   */
  private renderHandTiles(
    startX: number,
    y: number,
    hand: THand,
    winTileId: number | null
  ): number {
    const parts = splitResultHand(hand, winTileId);
    let x = startX;

    for (const meld of parts.melds) {
      x = this.renderMeld(x, y, meld);
      x += MELD_GAP;
    }

    x = this.renderTileRun(x, y, parts.concealed);

    if (parts.winTile) {
      x += WIN_TILE_GAP;
      x = this.renderTileRun(x, y, [parts.winTile]);
    }

    if (parts.flowers.length > 0) {
      x += FLOWER_GROUP_GAP;
      x = this.renderTileRun(x, y, parts.flowers);
    }

    return x;
  }

  /** 吃／碰依原 APP [中、來源、尾] 排列；槓的第四張疊在中間牌上方。 */
  private renderMeld(startX: number, y: number, meld: TMeld): number {
    if (
      meld.type === MeldType.KongExposed ||
      meld.type === MeldType.KongConcealed ||
      meld.type === MeldType.KongAdded
    ) {
      const baseTiles = meld.tiles.slice(0, 3);
      const nextX = this.renderTileRun(startX, y, baseTiles);
      const stacked = meld.tiles[3];
      if (stacked) {
        const middleX = startX + TILE_STEP;
        const sprite = new CTileSprite(
          this,
          middleX,
          y - KONG_STACK_OFFSET_Y,
          stacked,
          TileFace.HandBottom
        );
        sprite.setScale(TILE_SCALE);
        sprite.setDepth(2);
      }
      return nextX;
    }

    const [first, second, third] = meld.tiles;
    const ordered = [second, first, third].filter((tile): tile is TTile => Boolean(tile));
    return this.renderTileRun(startX, y, ordered);
  }

  /** 畫一排牌（正面），回傳下一個可用 X。 */
  private renderTileRun(startX: number, y: number, tiles: ReadonlyArray<TTile>): number {
    let x = startX;
    for (const tile of tiles) {
      const sprite = new CTileSprite(this, x, y, tile, TileFace.HandBottom);
      sprite.setScale(TILE_SCALE);
      x += TILE_STEP;
    }
    return x;
  }

  private getWinTileId(seat: TSeat): number | null {
    if (this.flow.summary.kind !== RoundSummaryKind.Hu) return null;
    return this.flow.summary.results.find((result) => result.seat === seat)?.winTile.id ?? null;
  }

  /** 依 summary 在牌列右側貼「自摸」／「胡」／「放槍」章。 */
  private renderTag(seat: TSeat, tilesEndX: number, centerY: number): void {
    if (this.flow.summary.kind !== RoundSummaryKind.Hu) return;
    const results = this.flow.summary.results;

    const winResult = results.find((result) => result.seat === seat);
    const isLoser = results.some((r) => r.loserSeat === seat);

    let key: string | null = null;
    if (winResult?.isSelfDraw) key = RESULT_TAG_SELF_DRAW_KEY;
    else if (winResult) key = RESULT_TAG_HU_KEY;
    else if (isLoser) key = RESULT_TAG_PAO_KEY;
    if (!key) return;

    const tagX = Math.min(tilesEndX + TAG_GAP, LOGICAL_WIDTH - TAG_RIGHT_MARGIN);
    this.add.image(tagX, centerY, key).setOrigin(0.5).setScale(TAG_SCALE);
  }

  /** 流局指示圖（畫面中央）。 */
  private renderDrawBanner(): void {
    this.add
      .image(SCREEN_CENTER_X, DRAW_BANNER_Y, RESULT_DRAW_KEY)
      .setOrigin(0.5)
      .setScale(DRAW_BANNER_SCALE)
      .setDepth(DRAW_BANNER_DEPTH);
  }

  // --------------------------------------------------------------------------
  // 「請碰觸螢幕繼續」閃爍
  // --------------------------------------------------------------------------
  private renderPressHint(): void {
    const hint = this.add
      .text(SCREEN_CENTER_X, PRESS_HINT_Y, PRESS_HINT_TEXT, {
        ...FONT_STYLES.body,
        fontSize: PRESS_HINT_FONT_SIZE,
        color: PAGE_THEME.goldText,
      })
      .setOrigin(0.5)
      .setAlpha(0);

    this.tweens.add({
      targets: hint,
      alpha: 1,
      duration: 500,
      onComplete: () => {
        this.tweens.add({
          targets: hint,
          alpha: { from: 1, to: 0.35 },
          duration: 1000,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.inOut',
        });
      },
    });
  }

  // --------------------------------------------------------------------------
  // 導航：胡 → 算台；流局 → 直接籌碼
  // --------------------------------------------------------------------------
  private advance(): void {
    playUIClick(this);
    const next =
      this.flow.summary.kind === RoundSummaryKind.Hu ? SceneKey.ResultPoint : SceneKey.ResultMoney;
    this.scene.start(next, this.flow);
  }
}
